/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { PackageCreateRequest } from '../models/package-create-request';
import { PackageCreateResponse } from '../models/package-create-response';
import { PackageDetailedResponse } from '../models/package-detailed-response';
import { PackageUpdateRequest } from '../models/package-update-request';
import { PagePackageAdminResponse } from '../models/page-package-admin-response';
import { PagePackagePageResponse } from '../models/page-package-page-response';
import { StatusUpdateRequest } from '../models/status-update-request';

/**
 * Package resource endpoints.
 */
@Injectable({
  providedIn: 'root',
})
export class PackagesService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /**
   * Path part for operation getPackageById
   */
  static readonly GetPackageByIdPath = '/packages/{packageId}';

  /**
   * Gets package.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackageById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackageById$Response(
    params: {
      packageId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PackageDetailedResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackagesService.GetPackageByIdPath,
      'get'
    );
    if (params) {
      rb.path('packageId', params.packageId, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PackageDetailedResponse>;
        })
      );
  }

  /**
   * Gets package.
   *
   * Finds and returns the saved resource with the specified id.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackageById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackageById(
    params: {
      packageId: string;
    },
    context?: HttpContext
  ): Observable<PackageDetailedResponse> {
    return this.getPackageById$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PackageDetailedResponse>) =>
          r.body as PackageDetailedResponse
      )
    );
  }

  /**
   * Path part for operation updatePackage
   */
  static readonly UpdatePackagePath = '/packages/{packageId}';

  /**
   * Updates a package.
   *
   * Updates an existing package based on its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePackage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePackage$Response(
    params: {
      packageId: string;
      body: PackageUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackagesService.UpdatePackagePath,
      'put'
    );
    if (params) {
      rb.path('packageId', params.packageId, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates a package.
   *
   * Updates an existing package based on its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePackage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePackage(
    params: {
      packageId: string;
      body: PackageUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updatePackage$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation updatePackageStatus
   */
  static readonly UpdatePackageStatusPath = '/admin/packages/status';

  /**
   * Updates the status of package by admin.
   *
   * Updates the status of package based on the provided list of package ids and a status.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updatePackageStatus()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePackageStatus$Response(
    params: {
      body: StatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<void>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackagesService.UpdatePackageStatusPath,
      'put'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'text',
          accept: '*/*',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return (r as HttpResponse<any>).clone({
            body: undefined,
          }) as StrictHttpResponse<void>;
        })
      );
  }

  /**
   * Updates the status of package by admin.
   *
   * Updates the status of package based on the provided list of package ids and a status.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updatePackageStatus$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updatePackageStatus(
    params: {
      body: StatusUpdateRequest;
    },
    context?: HttpContext
  ): Observable<void> {
    return this.updatePackageStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation getPackages
   */
  static readonly GetPackagesPath = '/packages';

  /**
   * Gets packages.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackages()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackages$Response(
    params: {
      /**
       * There are cases when we need to filter on packages by their ID
       */
      id?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Filter on amount
       */
      amount?: number;

      /**
       * Filter on mainType of package
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of package
       */
      subType?: string;

      /**
       * Filter on publication status of packages
       */
      status: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagePackagePageResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackagesService.GetPackagesPath,
      'get'
    );
    if (params) {
      rb.query('id', params.id, {});
      rb.query('name', params.name, {});
      rb.query('unit', params.unit, {});
      rb.query('amount', params.amount, {});
      rb.query('mainType', params.mainType, {});
      rb.query('subType', params.subType, {});
      rb.query('status', params.status, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagePackagePageResponse>;
        })
      );
  }

  /**
   * Gets packages.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackages$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackages(
    params: {
      /**
       * There are cases when we need to filter on packages by their ID
       */
      id?: string;

      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on unit
       */
      unit?: 'LITERS' | 'KILOGRAMS';

      /**
       * Filter on amount
       */
      amount?: number;

      /**
       * Filter on mainType of package
       */
      mainType?: 'STANDALONE' | 'COLLECTION';

      /**
       * Filter on subType of package
       */
      subType?: string;

      /**
       * Filter on publication status of packages
       */
      status: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagePackagePageResponse> {
    return this.getPackages$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagePackagePageResponse>) =>
          r.body as PagePackagePageResponse
      )
    );
  }

  /**
   * Path part for operation createPackage
   */
  static readonly CreatePackagePath = '/packages';

  /**
   * Creates a new package.
   *
   * Creates a new package and returns the saved resource with its identifier.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createPackage()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPackage$Response(
    params: {
      body: PackageCreateRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PackageCreateResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackagesService.CreatePackagePath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PackageCreateResponse>;
        })
      );
  }

  /**
   * Creates a new package.
   *
   * Creates a new package and returns the saved resource with its identifier.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createPackage$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createPackage(
    params: {
      body: PackageCreateRequest;
    },
    context?: HttpContext
  ): Observable<PackageCreateResponse> {
    return this.createPackage$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PackageCreateResponse>) =>
          r.body as PackageCreateResponse
      )
    );
  }

  /**
   * Path part for operation getPackagesForAdmin
   */
  static readonly GetPackagesForAdminPath = '/admin/packages';

  /**
   * Gets packages for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getPackagesForAdmin()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackagesForAdmin$Response(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on merchantName
       */
      merchantName?: string;

      /**
       * Filter on status
       */
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<PagePackageAdminResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      PackagesService.GetPackagesForAdminPath,
      'get'
    );
    if (params) {
      rb.query('name', params.name, {});
      rb.query('merchantName', params.merchantName, {});
      rb.query('status', params.status, {});
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'application/json',
          context: context,
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<PagePackageAdminResponse>;
        })
      );
  }

  /**
   * Gets packages for admin.
   *
   * Finds and returns the saved resources with its identifiers.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getPackagesForAdmin$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getPackagesForAdmin(
    params?: {
      /**
       * Filter on name
       */
      name?: string;

      /**
       * Filter on merchantName
       */
      merchantName?: string;

      /**
       * Filter on status
       */
      status?: 'PUBLISHED' | 'DRAFT' | 'REJECTED' | 'ONGOING';

      /**
       * Zero-based page index (0..N)
       */
      page?: number;

      /**
       * The size of the page to be returned
       */
      size?: number;

      /**
       * Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
       */
      sort?: Array<string>;
    },
    context?: HttpContext
  ): Observable<PagePackageAdminResponse> {
    return this.getPackagesForAdmin$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<PagePackageAdminResponse>) =>
          r.body as PagePackageAdminResponse
      )
    );
  }
}
